import { computed, getCurrentInstance, onUnmounted, ref, unref, watch } from "vue";
import {
  bulkResetCheckpointInspections,
  getCheckpointInspectionsByVersion,
  resetCheckPointEvaluation,
} from "@/api/proficiencyAPI";
import {
  DEFAULT_INSPECTION_FILTERS,
  inspectionFilterFieldError,
  mapInspectionsPayload,
  syncPayloadFromChips,
  toChipFilters,
  toQueryParams,
} from "@/utils/mapCheckpointInspectionsByVersion";

function emptyMappedPayload() {
  return {
    inspectionCount: 0,
    totalCount: 0,
    canBulkReset: false,
    resetGroup: [],
    versionGroups: [],
  };
}

/**
 * Loads GET inspections-by-version for the Checkpoint Inspections tab (ER-11594).
 * Does not fetch drafts or when `enabled` is false (flag off / tab hidden).
 */
export function useCheckpointInspectionsByVersion({
  accountId,
  responsibilityId,
  checkpointId,
  accessId,
  enabled,
  notifyError,
} = {}) {
  const versionGroups = ref([]);
  const resetGroup = ref([]);
  const canBulkReset = ref(false);
  const inspectionCount = ref(0);
  const totalCount = ref(0);
  const hasLoaded = ref(false);
  const unavailable = ref(false);
  const loading = ref(false);
  const filterPayload = ref({ ...DEFAULT_INSPECTION_FILTERS });
  const employeeSearch = ref("");
  const filterModalOpen = ref(false);
  const SEARCH_DEBOUNCE_MS = 500;
  let searchDebounceTimer = null;

  function cancelSearchDebounce() {
    if (searchDebounceTimer == null) return;
    clearTimeout(searchDebounceTimer);
    searchDebounceTimer = null;
  }

  const chipFilters = computed(() => toChipFilters(filterPayload.value));

  function resolve(value) {
    const resolved = typeof value === "function" ? value() : unref(value);
    return resolved;
  }

  function context() {
    return {
      accountId: resolve(accountId),
      responsibilityId: resolve(responsibilityId),
      checkpointId: resolve(checkpointId),
      enabled: Boolean(resolve(enabled)),
    };
  }

  function canFetch(ctx = context()) {
    return (
      ctx.enabled &&
      !unavailable.value &&
      ctx.accountId != null &&
      ctx.accountId !== "" &&
      ctx.responsibilityId != null &&
      ctx.checkpointId != null
    );
  }

  function applyMapped(mapped = emptyMappedPayload(), { loaded = true } = {}) {
    versionGroups.value = mapped.versionGroups;
    resetGroup.value = mapped.resetGroup;
    canBulkReset.value = mapped.canBulkReset;
    inspectionCount.value = mapped.inspectionCount;
    totalCount.value = mapped.totalCount;
    hasLoaded.value = loaded;
  }

  async function fetchInspections() {
    const ctx = context();
    if (!canFetch(ctx)) return;

    loading.value = true;
    try {
      const params = toQueryParams(filterPayload.value, employeeSearch.value);
      const response = await getCheckpointInspectionsByVersion(
        ctx.accountId,
        ctx.responsibilityId,
        ctx.checkpointId,
        params
      );
      applyMapped(mapInspectionsPayload(response?.data || {}));
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403 || status === 404) {
        unavailable.value = true;
        return;
      }
      const message =
        inspectionFilterFieldError(error) || "Failed to load inspections. Please try again.";
      notifyError?.(message);
    } finally {
      loading.value = false;
    }
  }

  function applyFilters(payload) {
    cancelSearchDebounce();
    filterPayload.value = { ...DEFAULT_INSPECTION_FILTERS, ...(payload || {}) };
    filterModalOpen.value = false;
    return fetchInspections();
  }

  function applyChipFilters(nextChips) {
    const next = syncPayloadFromChips(filterPayload.value, nextChips || {});
    if (JSON.stringify(next) === JSON.stringify(filterPayload.value)) return;
    cancelSearchDebounce();
    filterPayload.value = next;
    return fetchInspections();
  }

  function setEmployeeSearch(value) {
    const next = value ?? "";
    if (next === employeeSearch.value) return;
    employeeSearch.value = next;
    cancelSearchDebounce();
    searchDebounceTimer = setTimeout(() => {
      searchDebounceTimer = null;
      fetchInspections();
    }, SEARCH_DEBOUNCE_MS);
  }

  function resetAllFilters() {
    cancelSearchDebounce();
    filterPayload.value = { ...DEFAULT_INSPECTION_FILTERS };
    employeeSearch.value = "";
    return fetchInspections();
  }

  async function resetInspectionRow({ employeeId } = {}) {
    const ctx = context();
    if (!canFetch(ctx) || employeeId == null) return false;
    loading.value = true;
    try {
      await resetCheckPointEvaluation(ctx.accountId, ctx.responsibilityId, ctx.checkpointId, {
        employee: employeeId,
        checkpoint: ctx.checkpointId,
        reason: "reset",
        status: "reset",
      });
    } catch (error) {
      loading.value = false;
      throw error;
    }
    await fetchInspections();
    return true;
  }

  /**
   * POST bulk-reset for every inspection on the checkpoint (all versions).
   * Body matches per-row reset: `{ reason: "reset", access_id }`.
   */
  async function bulkResetInspections(reason = "reset") {
    const ctx = context();
    const trimmedReason = String(reason || "reset").trim() || "reset";
    const access = resolve(accessId);
    if (!canFetch(ctx) || access == null || access === "") {
      const error = new Error("Unable to reset inspections: missing checkpoint context.");
      throw error;
    }
    loading.value = true;
    try {
      await bulkResetCheckpointInspections(ctx.accountId, ctx.responsibilityId, ctx.checkpointId, {
        reason: trimmedReason,
        access_id: access,
      });
    } catch (error) {
      loading.value = false;
      throw error;
    }
    await fetchInspections();
    return true;
  }

  /**
   * Primitive key so a new `checkpoint` object with the same id does not refetch.
   * Watching `[id, account, …]` (new array each run) retried on every parent render.
   */
  const fetchKey = computed(() => {
    const ctx = context();
    return `${ctx.enabled ? 1 : 0}|${ctx.accountId ?? ""}|${ctx.responsibilityId ?? ""}|${
      ctx.checkpointId ?? ""
    }`;
  });

  watch(
    fetchKey,
    (key, previousKey) => {
      const ctx = context();
      const prevCheckpointId = previousKey == null ? null : previousKey.split("|")[3];
      const nextCheckpointId = String(ctx.checkpointId ?? "");
      if (previousKey != null && nextCheckpointId !== prevCheckpointId) {
        unavailable.value = false;
        applyMapped(emptyMappedPayload(), { loaded: false });
      }
      if (!ctx.enabled) {
        cancelSearchDebounce();
        return;
      }
      fetchInspections();
    },
    { immediate: true }
  );

  if (getCurrentInstance()) {
    onUnmounted(cancelSearchDebounce);
  }

  return {
    versionGroups,
    resetGroup,
    canBulkReset,
    inspectionCount,
    totalCount,
    hasLoaded,
    unavailable,
    loading,
    filterPayload,
    chipFilters,
    employeeSearch,
    filterModalOpen,
    fetchInspections,
    applyFilters,
    applyChipFilters,
    setEmployeeSearch,
    resetAllFilters,
    resetInspectionRow,
    bulkResetInspections,
  };
}
